import { useState, useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import cn from 'classnames';
// Context
import { useStreamContext } from 'hooks';
// Hooks
import { useConfirmCardPayment } from 'hooks';
import { useGetCurrUser } from 'hooks/useGetCurrUser';
import { useBtcPrice } from 'hooks/useBtcPrice';
// Api
import apolloChatClient from 'api/apolloChatClient';
import { ADD_STREAM_CHAT_V2 } from 'api/chat/mutations';
import {
  CREATE_STREAM_TIP,
  CREATE_STREAM_TIP_BITCOIN,
} from 'api/store/mutations';
import { GET_TIP_MESSAGE_PRESETS } from 'api/tips/queries';
// Types
import {
  CreateStreamTip,
  CreateStreamTipVariables,
} from 'api/store/types/CreateStreamTip';
import {
  CreateStreamTipBitcoin,
  CreateStreamTipBitcoin_createStreamTipBitcoin_payment_CryptoPayment,
  CreateStreamTipBitcoinVariables,
} from 'api/store/types/CreateStreamTipBitcoin';
import {
  AddStreamChatV2,
  AddStreamChatV2Variables,
} from 'api/chat/types/AddStreamChatV2';
import { ChatType } from 'api/graphql-chat-global-types';
import { GetTipMessagePresets_getTipMessagePresets } from 'api/tips/types/GetTipMessagePresets';
// Constants
import { TERMS_OF_SERVICE } from 'constants/routes';
// Helpers
import { getStreamerName } from 'helpers/streams';
// Ui3
import Button from 'ui3/Button/Button';
import TextField from 'ui3/TextField/TextField';
import Text from 'ui3/Text/Text';
import CheckBox from 'ui3/CheckBox/CheckBox';
import Select from 'ui3/Select/Select';
// Components
import { showToast } from 'components/common/Toast/Toast';
import ProductPayment from 'components/common3/ProductPayment/ProductPayment';
import BitcoinPaymentDisplay from 'components/BitcoinPayment/BitcoinPaymentDisplay';
// Styles
import styles from './TipStoreV2.module.scss';

type TipStoreProps = {
  onCloseModal: () => void;
  onBitcoinPaymentConfirmed?: () => void;
};

type FormInputs = {
  amount: number;
  message: string;
};

type PaymentType = 'cash' | 'crypto';

interface BitcoinPaymentData {
  invoiceUrl: string;
  tipId: string;
  amount: number;
  message?: string;
}

export type TipMessagePresetSelectOption = {
  value: string;
  label: string;
};

const cashValidationSchema = yup.object().shape({
  amount: yup
    .number()
    .min(1, 'Please enter an amount higher than $1.00')
    .transform((value) => (isNaN(value) ? undefined : Number(value)))
    .required('Tip Amount is required field.'),
  message: yup
    .string()
    .max(1000, "Maximum number of characters can't be more than 1000"),
});

const cryptoValidationSchema = yup.object().shape({
  amount: yup
    .number()
    .min(0.0001, 'Please enter an amount higher than ₿0.0001')
    .transform((value) => (isNaN(value) ? undefined : Number(value)))
    .test('max-decimals', 'Amount can have up to 8 decimal places', (value) => {
      if (value === undefined || value === null) return true;
      const decimals = value.toString().split('.')[1];
      return !decimals || decimals.length <= 8;
    })
    .required('Tip Amount is required field.'),
  message: yup
    .string()
    .max(1000, "Maximum number of characters can't be more than 1000"),
});

const defaultErrorMessage =
  'Looks like something went wrong. Please double-check all fields and try again';

const PRESET_AMOUNTS = [5, 10, 25, 50, 100];

const CRYPTO_OPTIONS = ['BTC'];

const cryptoIconMap = {
  BTC: 'bitcoin',
};

const TipStore = ({
  onCloseModal,
  onBitcoinPaymentConfirmed,
}: TipStoreProps) => {
  const { stream } = useStreamContext();

  const [paymentMethodId, setPaymentMethodId] = useState<string>('');
  const [selectedPresetAmount, setSelectedPresetAmount] = useState<
    number | null
  >(null);
  const [paymentType, setPaymentType] = useState<PaymentType>('cash');

  const [
    cryptoPaymentData,
    setCryptoPaymentData,
  ] = useState<BitcoinPaymentData | null>(null);

  const [showBitcoinPayment, setShowBitcoinPayment] = useState(false);
  const [tosAccepted, setTosAccepted] = useState<boolean>(false);

  const { data: user } = useGetCurrUser();
  const {
    fetchBtcPrice,
    convertBtcToUsd,
    loading: priceLoading,
  } = useBtcPrice();
  const hasAcceptedTos = user?.me?.termsOfServiceOptIn;

  const [createStreamTip, { loading: createStreamTipLoading }] = useMutation<
    CreateStreamTip,
    CreateStreamTipVariables
  >(CREATE_STREAM_TIP);

  const [
    createStreamTipBitcoin,
    { loading: createStreamTipBitcoinLoading },
  ] = useMutation<CreateStreamTipBitcoin, CreateStreamTipBitcoinVariables>(
    CREATE_STREAM_TIP_BITCOIN
  );

  const {
    confirmCardPayment,
    loading: stripeLoading,
  } = useConfirmCardPayment();

  const showError = (message: string = defaultErrorMessage) => {
    showToast({
      message,
      type: 'error',
    });
  };

  const streamId = stream?.id || '';
  const storeName = getStreamerName(stream?.store);

  // Check if chat is enabled - chat should be enabled if chatEnabled is not false
  // For interviews, check interview.chatEnabled first, then fall back to stream.chatEnabled
  // chatEnabled can be null (default enabled), true (explicitly enabled), or false (disabled)
  const isChatEnabled =
    stream?.interview?.chatEnabled !== false && stream?.chatEnabled !== false;

  const [addStreamChatV2, { loading: sendChatMessageLoading }] = useMutation<
    AddStreamChatV2,
    AddStreamChatV2Variables
  >(ADD_STREAM_CHAT_V2, {
    client: apolloChatClient,
  });

  const {
    watch,
    reset,
    register,
    handleSubmit,
    setValue,
    control,
    errors: formErrors,
  } = useForm<FormInputs>({
    resolver: yupResolver(
      paymentType === 'crypto' ? cryptoValidationSchema : cashValidationSchema
    ),
    mode: 'onChange',
  });

  const amount = watch('amount');
  const message = watch('message');

  const { data: presetsData } = useQuery(GET_TIP_MESSAGE_PRESETS);
  const presets = (presetsData?.getTipMessagePresets || []).filter(
    (p: GetTipMessagePresets_getTipMessagePresets) => p.isActive
  );

  const handlePresetAmountClick = (presetAmount: number) => {
    if (selectedPresetAmount === presetAmount) {
      setValue('amount', undefined);
      setSelectedPresetAmount(null);
    } else {
      setValue('amount', presetAmount);
      setSelectedPresetAmount(presetAmount);
    }
  };

  const handleAmountInputChange = useCallback(() => {
    setSelectedPresetAmount(null);
    if (paymentType === 'crypto') {
      fetchBtcPrice();
    }
  }, [paymentType, fetchBtcPrice]);

  const handlePaymentTypeChange = (type: string) => {
    setPaymentType(type as PaymentType);
    reset();
  };

  const handleStreamChatMessageSend = async (
    tipAmount?: number,
    isCrypto?: boolean
  ): Promise<void> => {
    try {
      // Only send chat messages if chat is enabled
      if (isChatEnabled) {
        const amountToSend = tipAmount || amount;

        if (amountToSend !== undefined) {
          const prefix = isCrypto ? 'crypto:' : 'cash:';
          const chatMessage = `${prefix}${amountToSend.toString()}`;

          await addStreamChatV2({
            variables: {
              stream_id: streamId,
              chat_type: ChatType.Tipped,
              chat_message: chatMessage,
            },
          });
        }

        if (message || cryptoPaymentData?.message) {
          await addStreamChatV2({
            variables: {
              stream_id: streamId,
              chat_type: ChatType.Chat,
              chat_message: message || (cryptoPaymentData?.message as string),
            },
          });

          showToast({
            type: 'success',
            message: `You've successfully sent a chat message and tipped ${storeName}`,
          });
          onCloseModal();
          reset();
          return;
        }
      }

      // If chat is disabled or no message, just show success toast
      if (!message) {
        showToast({
          type: 'success',
          message: `You've successfully tipped ${storeName}`,
        });
      }

      onCloseModal();
      reset();
    } catch (err) {
      showError(err?.message);
    }
  };

  const handlePaymentSuccess = async (
    tipAmount?: number,
    isCrypto?: boolean
  ) => {
    await handleStreamChatMessageSend(tipAmount, isCrypto);
  };

  const handleOnPaymentSent = () => {
    setShowBitcoinPayment(false);
    handlePaymentSuccess(cryptoPaymentData?.amount, true);
    setCryptoPaymentData(null);
  };

  const handleConfettiStart = () => {
    if (onBitcoinPaymentConfirmed) {
      onBitcoinPaymentConfirmed();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ',' || e.key === '-' || e.key === '+' || e.key === 'e') {
      e.preventDefault();
    }
    if (e.key === '.') {
      if (paymentType !== 'crypto') {
        e.preventDefault();
      }
    }
  };

  const handleCashPayment: SubmitHandler<FormInputs> = async (): Promise<void> => {
    try {
      if (paymentMethodId) {
        if (!hasAcceptedTos && !tosAccepted) {
          showError('You must agree to the Tipping Terms to continue');
          return;
        }
        const { data } = await createStreamTip({
          variables: {
            input: {
              message,
              streamId,
              amount: +amount,
              termsOfServiceOptIn: tosAccepted,
            },
          },
        });

        const stripeIntentClientSecret =
          data?.createStreamTip?.stripeIntentClientSecret;

        if (stripeIntentClientSecret) {
          await confirmCardPayment({
            paymentMethodId,
            stripeIntentClientSecret,
            onErrorCallback: showError,
            onSuccessCallback: () => handlePaymentSuccess(+amount, false),
          });
        } else {
          showError();
        }
      }
    } catch (err) {
      showError();
    }
  };

  const handleCryptoPayment: SubmitHandler<FormInputs> = async (): Promise<void> => {
    try {
      if (!hasAcceptedTos && !tosAccepted) {
        showError('You must agree to the Tipping Terms to continue');
        return;
      }
      const { data } = await createStreamTipBitcoin({
        variables: {
          input: {
            streamId,
            message,
            amount: +amount,
            termsOfServiceOptIn: tosAccepted,
          },
        },
      });

      const tipId = data?.createStreamTipBitcoin?.id;
      const payment = data?.createStreamTipBitcoin?.payment;

      if (payment?.__typename !== 'CryptoPayment' || !tipId) {
        showError('Failed to create Bitcoin invoice');
      }
      const paymentWithNewFields = payment as CreateStreamTipBitcoin_createStreamTipBitcoin_payment_CryptoPayment;
      const { bitcoinInvoiceUrl } = paymentWithNewFields;

      if (!bitcoinInvoiceUrl) {
        showError('Failed to create Bitcoin payment invoice');
      }

      setCryptoPaymentData({
        invoiceUrl: bitcoinInvoiceUrl,
        tipId: tipId as string,
        amount: +amount,
        message: message,
      });

      setShowBitcoinPayment(true);
    } catch (err) {
      showError();
    }
  };

  const handlePayment: SubmitHandler<FormInputs> = async (
    data
  ): Promise<void> => {
    if (paymentType === 'cash') {
      await handleCashPayment(data);
    } else {
      await handleCryptoPayment(data);
    }
  };

  const isCtaLoading =
    stripeLoading ||
    createStreamTipLoading ||
    createStreamTipBitcoinLoading ||
    sendChatMessageLoading;

  const isDisabled = paymentType === 'cash' ? !paymentMethodId : false;

  if (!stream?.store) {
    return null;
  }

  if (showBitcoinPayment && cryptoPaymentData) {
    return (
      <>
        <BitcoinPaymentDisplay
          invoiceUrl={cryptoPaymentData.invoiceUrl}
          tipId={cryptoPaymentData.tipId}
          amount={cryptoPaymentData.amount}
          orderType="stream_tip"
          onPaymentSent={handleOnPaymentSent}
          onConfettiStart={handleConfettiStart}
        />
      </>
    );
  }

  return (
    <>
      <form className={styles.form} aria-label={`Tip to ${storeName}`}>
        <Text className={styles.header}>
          {`Send a tip to ${storeName} to show your appreciation!`}
        </Text>

        {/* Payment Type Tabs */}
        <div className={styles.paymentTypeTabs}>
          <Button
            type="button"
            variant={'secondary'}
            className={cn(styles.tabButton, {
              [styles.tabButtonActive]: paymentType === 'cash',
            })}
            onClick={() => handlePaymentTypeChange('cash')}
          >
            Cash
          </Button>
          <Button
            type="button"
            variant={'secondary'}
            className={cn(styles.tabButton, {
              [styles.tabButtonActive]: paymentType === 'crypto',
            })}
            onClick={() => handlePaymentTypeChange('crypto')}
          >
            Crypto
          </Button>
        </div>

        <div className={styles.body}>
          <div className={styles.bodySection}>
            <div className={styles.tipAmountSection}>
              <Text className={styles.subject} variant="body2Medium">
                Tipping Amount
              </Text>
              {paymentType === 'crypto' ? (
                <div style={{ position: 'relative' }}>
                  <TextField
                    className={styles.amountInput}
                    name="amount"
                    ref={register}
                    placeholder="0 BTC"
                    prefixIcon="bitcoin"
                    error={formErrors?.amount?.message}
                    type="number"
                    step="0.00000001"
                    onChange={handleAmountInputChange}
                    onKeyDown={handleKeyDown}
                  />
                  {amount > 0 && !formErrors?.amount && (
                    <div className={styles.usdValue}>
                      <Text variant="body1Regular16" color="lights-low">
                        {priceLoading
                          ? '...'
                          : convertBtcToUsd(amount)
                          ? `~$${convertBtcToUsd(amount)?.toLocaleString()}`
                          : ''}
                      </Text>
                    </div>
                  )}
                </div>
              ) : (
                <TextField
                  className={styles.amountInput}
                  name="amount"
                  ref={register}
                  prefixIcon="dollar"
                  placeholder="0"
                  error={formErrors?.amount?.message}
                  type="number"
                  onChange={handleAmountInputChange}
                  onKeyDown={handleKeyDown}
                />
              )}

              {paymentType === 'cash' && (
                <div className={styles.presetAmounts}>
                  {PRESET_AMOUNTS.map((presetAmount) => (
                    <Button
                      key={presetAmount}
                      type="button"
                      size="large"
                      variant="secondary"
                      className={cn(styles.presetAmountButton, {
                        [styles.presetAmountButtonActive]:
                          selectedPresetAmount === presetAmount,
                      })}
                      onClick={() => handlePresetAmountClick(presetAmount)}
                    >
                      ${presetAmount}
                    </Button>
                  ))}
                </div>
              )}

              {paymentType === 'crypto' && (
                <div className={styles.cryptoContainer}>
                  <Text
                    variant="captionRegular"
                    className={styles.minAmountNote}
                  >
                    Min. amount 0.0001 BTC
                  </Text>
                  <div className={styles.presetCrypto}>
                    {CRYPTO_OPTIONS.map((cryptoOption) => (
                      <Button
                        key={cryptoOption}
                        type="button"
                        size="large"
                        variant="secondary"
                        icon={cryptoIconMap[cryptoOption]}
                        className={cn(
                          styles.presetCryptoOptionButton,
                          styles.presetAmountButtonActive
                        )}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Text variant="body2Medium">Message</Text>
            <Controller
              control={control}
              name="message"
              render={({ onChange, value }) => (
                <Select
                  placeholder="Select your message"
                  options={presets.map(
                    (p: GetTipMessagePresets_getTipMessagePresets) => ({
                      label: p.text,
                      value: p.text,
                    })
                  )}
                  value={value ? { label: value, value } : null}
                  onChange={(opt: TipMessagePresetSelectOption) =>
                    onChange(opt ? opt.value : '')
                  }
                  isClearable
                  isSearchable={false}
                  error={formErrors?.message?.message}
                  menuPosition="fixed"
                />
              )}
            />
          </div>

          {paymentType === 'cash' && (
            <div className={styles.paymentSection}>
              <ProductPayment onSelect={setPaymentMethodId} />
            </div>
          )}
        </div>

        <div className={styles.footer}>
          {!hasAcceptedTos && (
            <div className={styles.tosRow}>
              <CheckBox
                name="tos"
                checked={tosAccepted}
                className={styles.tosCheckbox}
                onChange={(e) => setTosAccepted(e.target.checked)}
                label=""
              />
              <Text className={styles.tosText}>
                I understand this Tip is a voluntary gift with no goods,
                services, or benefits in return.{' '}
                <a
                  href={TERMS_OF_SERVICE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.tosLink}
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn more
                </a>
              </Text>
            </div>
          )}
          <Button
            onClick={handleSubmit(handlePayment)}
            loading={isCtaLoading}
            disabled={isDisabled || (!hasAcceptedTos && !tosAccepted)}
            type="submit"
          >
            Send Tip
          </Button>
        </div>
      </form>
    </>
  );
};

export default TipStore;
